import React, { useState, useRef, useCallback } from 'react';
import { Segment, Icon, Header } from 'semantic-ui-react';

/**
 * FileDropZone — Reusable drag-and-drop file selection component.
 * 
 * Props:
 *   accept       — string, e.g. ".pdf" or ".jpg,.png,.jpeg"
 *   multiple     — boolean, allow multi-file select (default true)
 *   onFilesSelected — callback(files: File[])
 *   label        — string, main drop zone label
 *   sublabel     — string, secondary text
 *   icon         — string, Semantic UI icon name (default "cloud upload")
 *   disabled     — boolean
 *   maxSizeMB    — number, max file size in MB (optional)
 *   previewSrc   — optional image URL shown in place of the icon once something is chosen.
 *                  For a picture, a filename is a poor confirmation: it proves a file was
 *                  selected, not that it was the right one. Callers hold the object URL (and
 *                  revoke it) because only they know the file's lifetime.
 */
const FileDropZone = ({
  accept = '*',
  multiple = true,
  onFilesSelected,
  label = 'Drop files here',
  sublabel = 'or click to browse',
  icon = 'cloud upload',
  disabled = false,
  maxSizeMB = null,
  compact = false,   // smaller square zone (icon + label only)
  heightPx = null,   // explicit min-height (e.g. 120, 180)
  previewSrc = null, // thumbnail of what's been chosen, shown instead of the icon
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragError, setDragError] = useState('');
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  const validateFiles = useCallback((files) => {
    const acceptedExtensions = accept === '*'
      ? null
      : accept.split(',').map(ext => ext.trim().toLowerCase());

    const validated = [];
    const errors = [];

    for (const file of files) {
      // Check extension
      if (acceptedExtensions) {
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!acceptedExtensions.includes(ext)) {
          errors.push(`${file.name}: unsupported format (expected ${accept})`);
          continue;
        }
      }
      // Check size
      if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
        errors.push(`${file.name}: exceeds ${maxSizeMB}MB limit`);
        continue;
      }
      validated.push(file);
    }

    if (errors.length > 0) {
      setDragError(errors.join('; '));
      setTimeout(() => setDragError(''), 4000);
    }

    return validated;
  }, [accept, maxSizeMB]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (!multiple && droppedFiles.length > 1) {
      setDragError('Only one file allowed');
      setTimeout(() => setDragError(''), 3000);
      return;
    }

    const valid = validateFiles(droppedFiles);
    if (valid.length > 0 && onFilesSelected) {
      onFilesSelected(valid);
    }
  }, [disabled, multiple, validateFiles, onFilesSelected]);

  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const handleFileInput = useCallback((e) => {
    const selected = Array.from(e.target.files || []);
    const valid = validateFiles(selected);
    if (valid.length > 0 && onFilesSelected) {
      onFilesSelected(valid);
    }
    // Reset input so re-selecting same file triggers change
    e.target.value = '';
  }, [validateFiles, onFilesSelected]);

  // With something chosen, the zone stops being an empty target and becomes a confirmation: the
  // picture gets the room, and the two text chunks sit beside it as a block rather than stacked
  // underneath a thumbnail. Reverts to the centred prompt mid-drag — while a file is over the
  // zone, what matters is that it reads as a drop target, not what is already in it.
  const showPreview = !!previewSrc && !isDragging;

  return (
    <div>
      <Segment
        placeholder
        textAlign={showPreview ? 'left' : 'center'}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: isDragging ? '2px dashed #2185d0' : '2px dashed #ccc',
          backgroundColor: isDragging ? '#f0f7ff' : (disabled ? '#f9f9f9' : '#fafafa'),
          transition: 'all 0.2s ease',
          minHeight: `${heightPx || (showPreview && !compact ? 180 : 120)}px`,
          height: heightPx ? `${heightPx}px` : undefined,
          padding: compact ? '0.5em' : undefined,
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: showPreview ? 'flex-start' : 'center',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {showPreview ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25em', width: '100%' }}>
            <img
              src={previewSrc}
              alt="Selected"
              style={{
                display: 'block',
                flex: '0 0 auto',
                maxHeight: compact ? '90px' : '160px',
                maxWidth: compact ? '90px' : '160px',
                objectFit: 'contain',
                borderRadius: '4px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.3)',
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: compact ? '0.9em' : '1.1em',
                fontWeight: 600,
                color: '#555',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {label}
              </div>
              {!compact && (
                <div style={{ fontSize: '0.9em', fontWeight: 300, color: '#767676', marginTop: '0.4em' }}>
                  {sublabel}
                </div>
              )}
            </div>
          </div>
        ) : (
          <Header icon style={{ margin: 0 }}>
            <Icon
              name={icon}
              size={compact ? 'large' : 'huge'}
              color={isDragging ? 'blue' : 'grey'}
              style={{ transition: 'color 0.2s ease' }}
            />
            <span style={{ fontSize: compact ? '0.9em' : '1.1em', color: isDragging ? '#2185d0' : '#555' }}>
              {isDragging ? 'Release to drop' : label}
            </span>
            {!compact && (
              <Header.Subheader style={{ marginTop: '0.5em' }}>
                {sublabel}
              </Header.Subheader>
            )}
          </Header>
        )}
      </Segment>

      {dragError && (
        <div style={{ color: '#db2828', fontSize: '0.9em', marginTop: '0.3em' }}>
          <Icon name="warning sign" /> {dragError}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileInput}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default FileDropZone;
